import React, { useEffect, useState } from "react"
import { Box, Button } from "@mui/material"
import { useTranslation } from "react-i18next"
import { Layout } from "src/components/layout"
import { useAuth } from "src/providers/LoginProvider"
import { Tag, TagService, User, UserService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import { useRecoilState } from "recoil"
import { EmployeeFilterModal } from "./EmployeeFilterModal"
import { Filter } from "./Filter"
import { EmployeeTable } from "./EmployeeTable"
import { FILTER_VALUES, SIDE_MENU, SNACKBAR_TYPES, SORT_ORDER, STATUS } from "src/modules/const"
import BreadCrumb from "src/components/atom/BreadCrumb"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import KeyboardDoubleArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined"
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined"
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined"
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined"
import "./index.scss"
import { useNavigate, useParams } from "react-router"
import { Top } from "../top"
import GenerateInvitationCodeModal from "./GenerateInvitationCodeModal"

const PAGE_SIZE = 20

export const EmployeeList = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })

  const { companyId } = useParams()

  const [list, setList] = useState<User[]>([])

  const { user } = useAuth()
  const [tags, setTags] = useState<Tag[]>([])
  useEffect(() => {
    if (user == null) {
      return
    }
    TagService.listTags(user.token, Number(companyId))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user, companyId])

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false)

  const [page, setPage] = useState(0)
  const [total, setTotal] = useState<number>(0)

  const [orderBy] = useState<string>("id")
  const [order] = useState<string>(SORT_ORDER.ASCENDING)

  const [name, setName] = useState<string>("")
  const [nameCommpareType, setNameCommpareType] = useState<string>("")
  const [id, setID] = useState<string>("")
  const [idCompareType, setIdCompareType] = useState<string>("")
  const [years, setYears] = useState<string>("")
  const [yearsCompareType, setYearsCompareType] = useState<string>("")
  const [filterTags, setFilterTags] = useState<number[]>([])
  const [employmentType, setEmploymentType] = useState<string>("")
  const [status, setStatus] = useState<boolean | undefined>(false)

  const [from, setFrom] = useState<number>(0)
  const [to, setTo] = useState<number>(0)
  const [pageCnt, setPageCnt] = useState<number>(0)

  const [reload, setReload] = useState<boolean>(false)

  const navigate = useNavigate()

  const setPaginations = (p: number) => {
    if (total > 0) {
      setFrom(p * PAGE_SIZE + 1)

      if (p == pageCnt - 1) {
        setTo(p * PAGE_SIZE + (total % PAGE_SIZE))
      } else {
        setTo(p * PAGE_SIZE + PAGE_SIZE)
      }
    }
  }

  const nextPage = () => {
    if (page < pageCnt - 1) {
      setPage(page + 1)
      setPaginations(page + 1)
    }
  }

  const prevPage = () => {
    if (page > 0) {
      setPage(page - 1)
      setPaginations(page - 1)
    }
  }

  const firstPage = () => {
    if (page != 0) {
      setPage(0)
      setPaginations(0)
    }
  }

  const lastPage = () => {
    if (page != pageCnt - 1) {
      setPage(pageCnt - 1)
    }
  }

  const handleGetEmployeeList = () => {
    setShowProgress(true)
    UserService.listUsers(
      user!.token,
      Number(companyId),
      PAGE_SIZE,
      page * PAGE_SIZE,
      orderBy,
      order,
      name,
      nameCommpareType,
      id,
      idCompareType,
      years,
      yearsCompareType,
      filterTags,
      employmentType,
      status
    )
      .then((res) => {
        setShowProgress(false)
        setList(res.users as User[])
        setTotal(res.count)

        let cnt = Math.ceil(res.count / PAGE_SIZE)
        setPageCnt(cnt)
        if (res.count! > 0) {
          setFrom(page * PAGE_SIZE + 1)

          if (page == cnt - 1) {
            setTo(res.count)
          } else {
            setTo(page * PAGE_SIZE + PAGE_SIZE)
          }
        } else {
          setFrom(0)
          setTo(0)
          setPageCnt(0)
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("failed_get_employee_list"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  const handleApply = (filters: Filter[]) => {
    setName("")
    setNameCommpareType("")
    setID("")
    setIdCompareType("")
    setYears("")
    setYearsCompareType("")
    setEmploymentType("")
    setFilterTags([])
    setStatus(undefined)

    filters.map((f) => {
      if (f.object == FILTER_VALUES.NAME) {
        setName(f.range)
        setNameCommpareType(f.condition)
      }
      if (f.object == FILTER_VALUES.ID) {
        setID(f.range)
        setIdCompareType(f.condition)
      }
      if (f.object == FILTER_VALUES.YEAR) {
        setYears(f.range)
        setYearsCompareType(f.condition)
      }
      if (f.object == FILTER_VALUES.TYPE) {
        setEmploymentType(f.range)
      }
      if (f.object == FILTER_VALUES.TAG) {
        setFilterTags(f.values)
      }
      if (f.object == FILTER_VALUES.STATUS) {
        setStatus(f.range == "none" ? undefined : f.range == STATUS.RESIGNED ? true : false)
      }
    })
    setReload(true)
  }

  const handleCsvExport = () => {
    if (user == null) {
      return
    }
    setShowProgress(true)

    UserService.exportUsers(
      user!.token,
      Number(companyId),
      name,
      nameCommpareType,
      id,
      idCompareType,
      years,
      yearsCompareType,
      filterTags,
      employmentType,
      status
    )
      .then((res) => {
        setShowProgress(false)
        window.Buffer = window.Buffer || require("buffer").Buffer
        const blob = new Blob([Buffer.from(res.toString(), "base64")], {
          type: "text/csv",
        })
        const url = window.URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.href = url
        a.download = "employee_list.csv"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        window.URL.revokeObjectURL(url)
      })
      .catch((e) => {
        console.log(e)
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_export"), type: SNACKBAR_TYPES.ERROR })
      })
    setShowProgress(false)
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    handleGetEmployeeList()
  }, [user, companyId, page])

  useEffect(() => {
    if (user == null) {
      return
    }
    if (!reload) {
      return
    }

    setReload(false)
    handleGetEmployeeList()
  }, [user, companyId, reload, name, id, years, employmentType, filterTags, status])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.EMPLOYEE_LIST}>
      <Box className="flex-container">
        <Box className="header">
          <BreadCrumb
            paths={[
              {
                name: t("list_title"),
                link: "",
              },
            ]}
          />
          <Box className="employee-filter-bar">
            <Box className="filter-section">
              <Button onClick={() => setFilterModalOpen(true)}>
                {t("filter")}
                <FilterAltIcon />
              </Button>
              <Button
                onClick={() => {
                  navigate(`/company/${companyId}/employee/import`)
                }}
              >
                {t("import")}
                <FileDownloadOutlinedIcon />
              </Button>
              <Button onClick={handleCsvExport}>
                {t("export")}
                <FileUploadOutlinedIcon />
              </Button>
              <GenerateInvitationCodeModal companyId={Number(companyId!)} tags={tags}>
                <span className="text-sm">招待コード生成</span>
              </GenerateInvitationCodeModal>
            </Box>
            <Box className="pagination-section">
              <Box className="pagination-description">
                {t("pagination", { total: total, from: from, to: to })}
              </Box>
              <Box className="pagination-buttons">
                <Button sx={{ width: "30px!important" }} disabled={page == 0} onClick={firstPage}>
                  <KeyboardDoubleArrowLeftOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button disabled={page == 0} onClick={prevPage}>
                  <KeyboardArrowLeftOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button disabled={pageCnt == 0 || page == pageCnt - 1} onClick={nextPage}>
                  <KeyboardArrowRightOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button
                  sx={{ mr: "0px !important" }}
                  disabled={pageCnt == 0 || page == pageCnt - 1}
                  onClick={lastPage}
                >
                  <KeyboardDoubleArrowRightOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="content">
          <EmployeeTable data={list} tags={tags} companyId={Number(companyId)}></EmployeeTable>
        </Box>
      </Box>
      <EmployeeFilterModal
        open={filterModalOpen}
        handleOpen={setFilterModalOpen}
        handleApply={handleApply}
        tags={tags}
      />
    </Layout>
  )
}
